import { Chip } from '@mui/material'
import {DataGrid, GridActionsCellItem, GridCellParams, GridColDef, GridRowParams} from '@mui/x-data-grid'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import useScreenWidth from '../../../../../shared/hooks/useScreenWidth'
import OffersCustomToolbar from './OffersCustomToolbar'
import OffersTagsCell from './OffersTagsCell'
import OffersTitleCell from './OffersTitleCell'
import ViewIcon from "@mui/icons-material/Visibility";
import {useNavigate} from "react-router";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

interface OffersDataGridProps {
  offers: any[]
  loading: boolean
  onEdit: (id: number) => void
  onDelete: (id: number) => void
  handleClickHighlight: (id: number, isHighlight: boolean) => void
  globalBrandId: number | null
}

export default function OffersDataGrid({ offers, loading, onEdit, onDelete, handleClickHighlight, globalBrandId }: OffersDataGridProps) {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const navigate = useNavigate()

  const getColumns = (): GridColDef[] => {
    return [
      {
        field: 'title',
        headerName: t('title'),
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridCellParams) => <OffersTitleCell params={params} handleClickHighlight={handleClickHighlight} />
      },
      {
        field: 'tags',
        width: 190,
        minWidth: 90,
        headerName: '',
        renderCell: (params: GridCellParams) => <OffersTagsCell params={params} />
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 70
      },
      {
        field: 'brand.name',
        headerName: t('brand'),
        width: 90,
        valueGetter: (params: GridCellParams) => (params.row.brand ? params.row.brand.name : ''),
        hideable: globalBrandId !== null
      },
      {
        field: 'language',
        headerName: t('language'),
        width: 90,
        renderCell: (params: GridCellParams) => (
          <Chip key={params.row.language.id} label={params.row.language.code.toUpperCase()} size="small" sx={{ maxWidth: '100%' }} />
        )
      },
      {
        field: 'role',
        headerName: t('roles'),
        width: 90,
        renderCell: (params: GridCellParams) => <span>{params.row.role.name}</span>
      },
      {
        field: 'date_ini',
        headerName: t('initial-date'),
        width: 110,
        renderCell: (params: GridCellParams) => {
          if (params.row.date_ini != null) {
            return dayjs(params.row.date_ini).format('DD MMM YYYY')
          }
          return '--'
        }
      },
      {
        field: 'date_end',
        headerName: t('final-date'),
        width: 110,
        renderCell: (params: GridCellParams) => {
          if (params.row.date_end != null) {
            return dayjs(params.row.date_end).format('DD MMM YYYY')
          }
          return '--'
        }
      },
      {
        field: 'actions',
        type: 'actions',
        sortable: false,
        filterable: false,
        width: 70,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            key={params.id}
            icon={<ViewIcon />}
            label={t('view')}
            onClick={() => {navigate(`${params.row.id}`)}}
            showInMenu
          />,
          <GridActionsCellItem
            key={params.id}
            icon={<EditIcon />}
            label={t('edit')}
            onClick={() => {onEdit(Number(params.id))}}
            showInMenu
          />,
          <GridActionsCellItem
            key={params.id}
            icon={<DeleteIcon />}
            label={t('delete')}
            onClick={() => {onDelete(Number(params.id))}}
            showInMenu
          />

        ]
      }
    ].filter(column => !(isMobile && ['tags', 'id', 'brand', 'language', 'role', 'date_ini', 'date_end'].includes(column.field)))
  }

  const columns = getColumns()

  return (
    <>
      <DataGrid
        rows={offers}
        columns={columns}
        pageSizeOptions={[5, 10, 20]}
        loading={loading}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 }
          }
        }}
        autoHeight
        density="comfortable"
        slots={{
          toolbar: OffersCustomToolbar
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 }
          }
        }}
        disableRowSelectionOnClick
      />
    </>
  )
}
